import React, { useState, useEffect, useRef } from 'react';
import { EditorState, DraftHandleValue, RichUtils, CompositeDecorator, ContentState, AtomicBlockUtils, Modifier } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createEmojiPlugin from '@draft-js-plugins/emoji';
import { toolbarPlugin } from './ToolbarComponent';

import '@draft-js-plugins/alignment/lib/plugin.css'; // Alignment plugin styles
import 'draft-js/dist/Draft.css';
import "./draft.scss";
import '@draft-js-plugins/emoji/lib/plugin.css';
import '@draft-js-plugins/static-toolbar/lib/plugin.css';
import { stateToHTML, } from 'draft-js-export-html';
import { Modal, Input, Button } from 'antd'
import { ReactMailChips } from "react-mail-chips";
import { ErrorMessages, useSelector, retrieveData } from '../../common/CommonExport';
import {
  onBoldClick, onItalicClick, onUnderlineClick,
  onBulletedListClick, onNumberedListClick, onUndo, onRedo, convertTo24HourFormat,
  addLinkRef, blockStyleFn, blockStyleFn1, applyAlignment
} from './draftHelpers';
import ComposeMailToolbar from './ComposeMailToolbar';
import EditorToolbar from './EditorToolbar';
import { resetSchedule, setEmailData, setIsError, setIsSubmitted, setIsSuccess, setSendBtnLoading } from '../../redux/slices/composeEmailSlice';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Attachment } from './utils/ComposeMailUtils';
import FilesUploadedList from './FilesUploadedList';
import createImagePlugin from "@draft-js-plugins/image";
import PopupModal from './modal/PopupModal';

const MAX_TOTAL_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

const emojiPlugin = createEmojiPlugin();
const { EmojiSuggestions, EmojiSelect } = emojiPlugin;

const imagePlugin = createImagePlugin();


const Link: React.FC<{
  contentState: ContentState;
  entityKey: string;
  children: React.ReactNode;
}> = (props) => {
  const { contentState, entityKey, children } = props;
  const { url } = contentState.getEntity(entityKey).getData();

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
};

const decorator = new CompositeDecorator([
  {
    strategy: (contentBlock, callback, contentState) => {
      contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        return (
          entityKey !== null &&
          contentState.getEntity(entityKey).getType() === 'LINK'
        );
      }, callback);
    },
    component: Link,
  },
]);

function capitalizeFirstLetter(inputText: any) {
  if (!inputText) return '';
  return inputText.charAt(0).toUpperCase() + inputText.slice(1);
};


const ComposeMail = (props: any) => {
  const { draftButton, cancelButton, handelCancel, submitButtonName, textToolBar, mainToolbar, actionType, isComposeEmail = false, defaultValidation = true, customFields, placeholder = "" } = props;
  const { scheduleDate, scheduleTime, sendBtnLoading, draftBtnLoading, isSuccess, isError, apiResponseMessage } = useSelector((state: any) => state.composeEmailSlice);
  const plugins: any = [emojiPlugin, toolbarPlugin, imagePlugin];
  const [editorState, setEditorState] = useState<any>(EditorState.createEmpty(decorator));
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [subject, setSubject] = useState<string>('');
  const [openCc, setOpenCc] = useState(false);
  const [openBcc, setOpenBcc] = useState(false);
  const [iconModal, setIconModal] = useState(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [attachedFile, setAttachedFile] = useState<File[]>([]);
  const [getAttachedFileNameList, setGetAttachedFileNameList] = useState<Attachment[]>([]);
  const [removedFiles, setRemovedFiles] = useState<Attachment[]>([]);
  const [linkModalVisible, setLinkModalVisible] = useState(false);
  const [imageLinkModalVisible, setImageLinkModalVisible] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [linkURL, setLinkURL] = useState('');
  const [modalErrMessage, setModalErrMessage] = useState('')
  const [popModal, setPopModal] = useState(false)
  const [modalResponse, setModalResponse] = useState('')
  const [modalButtonShow, setModalButtonShow] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null);
  const createdOn = new Date().toISOString();
  const userId = retrieveData('userId', true);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formattedScheduleDate = scheduleDate ? dayjs(scheduleDate).format('YYYY-MM-DD') : null;
  const formattedScheduleTime = scheduleTime ? dayjs(scheduleTime).format('hh:mm A') : null;
  const openLinkModal = () => setLinkModalVisible(true);
  const closeLinkModal = () => { setLinkModalVisible(false); setLinkURL(''); };

  useEffect(() => {
    dispatch(setEmailData(null));
    dispatch(resetSchedule());
  }, []);

  const handleClosePopUpModal = () => {
    setPopModal(false)
  }

  // Add link to the editor
  const addLink = async () => {
    if (linkURL.trim()) {
      let content = addLinkRef(linkURL.trim(), editorState);
      setEditorState(content);
    }
    // Close the link modal after applying the link
    closeLinkModal();
  };

  const handleIconModal = () => { setIconModal((prev) => !prev); };

  const validateEmailData = (defaultValidation: boolean, fields?: any) => {
    if (defaultValidation) {
      if (!emails.length) {
        setPopModal(true)
        setModalButtonShow(true)
        setModalErrMessage('Recipient email(s) cannot be empty.')
        setModalResponse('warning')
        return false;
      }
      if (!subject.trim()) {
        setPopModal(true)
        setModalButtonShow(true)
        setModalErrMessage('Subject cannot be empty.')
        setModalResponse('warning')
        return false;
      }
      if (!stateToHTML(editorState.getCurrentContent()).trim()) {
        setPopModal(true)
        setModalButtonShow(true)
        setModalErrMessage('Email body cannot be empty.')
        setModalResponse('warning')
        return false;
      }

    }

    // Dynamic validation base on the field entries
    if (fields) {
      for (const [key, value] of Object.entries(fields)) {
        if (!value) {
          setPopModal(true);
          setModalButtonShow(true);
          setModalErrMessage(`${capitalizeFirstLetter(key)} field cannot be empty.`);
          setModalResponse('warning');
          return false;
        }
      }
    }

    return true;
  };

  function replaceNewlineWithBr(input: string): string {
    return input.replace(/\n/g, "");
  }

  const handleToSendMail = (type: string) => {
    // Make sure default validation is passed, enabled or not
    if (validateEmailData(defaultValidation, customFields)) {
      const contentAsHtml = stateToHTML(editorState.getCurrentContent(), {
        blockStyleFn: blockStyleFn1, // Using blockStyleFn for text alignment
      });
      let checkSchedule = false;
      let processType = type;
      const payload: any = {
        senderCntUserId: userId,
        subject: subject,
        body: replaceNewlineWithBr(contentAsHtml),
        status: type,
        emailRecipient: {
          toEmail: emails.join(","),
          ccEmail: cc.length === 0 ? null : cc.join(","),
          bccEmail: bcc.length === 0 ? null : bcc.join(","),
        },
        createdOn: createdOn,
        customFields: customFields,
      };
      if (formattedScheduleDate && formattedScheduleTime) {
        const formattedTime24 = convertTo24HourFormat(formattedScheduleTime);
        const scheduledTime = `${formattedScheduleDate}T${formattedTime24}:00+05:30`;
        payload.scheduledTime = scheduledTime;
        payload.status = type === "DRAFT" ? "DRAFT" : "SCHEDULED";
        processType = type === "DRAFT" ? "DRAFT" : "SCHEDULED";
        checkSchedule = true;
      }
      if (actionType === "edit") {
        payload.attachments = getAttachedFileNameList;
      }
      const formData = new FormData();
      if (attachedFile && attachedFile.length > 0) {
        attachedFile.forEach((file) => {
          formData.append("files", file); // Append each file under the 'files' key
        });
      } else {
        formData.append("files", "");
      }

      if (payload) {
        dispatch(setEmailData(payload));
        dispatch(setIsSubmitted(true));
        dispatch(setSendBtnLoading(true));
      }
    }
  }

  useEffect(() => {
    if (isSuccess) {
      setPopModal(true);
      setModalButtonShow(false)
      setModalErrMessage(apiResponseMessage)
      setModalResponse('success')
      setTimeout(() => {
        handleClosePopUpModal()
        handelCancel(); // Close the modal after success
        dispatch(setIsSuccess(false));
        navigate("/user-dashboard/user-details"); // pass navigate Route
      }, 1500)
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setPopModal(true)
      setModalButtonShow(false)
      setModalErrMessage(apiResponseMessage ?? ErrorMessages.somethingWentWrong)
      setModalResponse('error')
      setTimeout(() => {
        handleClosePopUpModal()
        handelCancel();
        dispatch(setIsError(false));
      }, 1500)

    }
  }, [isError]);

  const handleKeyCommand = (command: any, state: any) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files); // Convert the FileList to an array and update state
      const currentTotalSize = attachedFile.reduce((acc, file) => acc + file.size, 0); // Calculate total size of already attached files
      const newFilesSize = newFiles.reduce((acc, file) => acc + file.size, 0); // Calculate size of new files

      if (currentTotalSize + newFilesSize > MAX_TOTAL_FILE_SIZE) {
        setPopModal(true)
        setModalButtonShow(false)
        setModalErrMessage('Total file size exceeds 10MB. Please remove some files or choose smaller files.')
        setModalResponse('error')
        setTimeout(() => {
          handleClosePopUpModal()
        }, 1500)
        return;
      }
      setAttachedFile(prevFiles => [...prevFiles, ...newFiles]);

      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 0);
    }
  };

  const handleInsertImageByURL = () => {
    if (!imageUrl) return;
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
    const isValidImage = imageExtensions.some(ext => imageUrl.toLowerCase().endsWith(ext));

    if (!isValidImage) {
      setPopModal(true)
      setModalButtonShow(true)
      setModalErrMessage("Invalid image URL. Please provide a valid image link.")
      setModalResponse('warning')
      return;
    }
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity("IMAGE", "IMMUTABLE", { src: imageUrl });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, " ");
    setEditorState(EditorState.forceSelection(newEditorState, newEditorState.getSelection()));

    setImageLinkModalVisible(false);
    setImageUrl("");
  };
  const handleFileSelect = () => {
    document.getElementById('announcementFileUpload')?.click();
  }
  const handleRemoveAttachment = (index: number) => {
    setAttachedFile((prevFiles) => prevFiles.filter((_, i: number) => i !== index));
  }
  // Wrapper function to handle updates
  const handleEmailsChange = (field: 'emails' | 'cc' | 'bcc', newEmails: string[]) => {
    const trimmedEmails = newEmails.map(email => email.trim());
    let duplicateEmails: string[] = [];
    const filteredEmails = trimmedEmails.filter(email => {
      const isDuplicate =
        (field === 'emails' && (cc.includes(email) || bcc.includes(email))) ||
        (field === 'cc' && (emails.includes(email) || bcc.includes(email))) ||
        (field === 'bcc' && (emails.includes(email) || cc.includes(email)));

      if (isDuplicate) duplicateEmails.push(email);
      return !isDuplicate;
    });

    if (duplicateEmails.length > 0) {
      setPopModal(true)
      setModalButtonShow(true)
      setModalErrMessage(`Duplicate email not allowed`)
      setModalResponse('warning')
    }

    if (field === 'emails') setEmails(filteredEmails);
    if (field === 'cc') setCc(filteredEmails);
    if (field === 'bcc') setBcc(filteredEmails);
  };
  // Function to handle click outside
  const handleClickOutside = (event: any) => {
    if (event) {
      setOpenCc(false);
      setOpenBcc(false)
    }
  };
  const removeAttachedFile = (index: number) => {
    const removedItem = getAttachedFileNameList[index];
    const updatedFilesList = getAttachedFileNameList.filter((_, i) => i !== index);
    setGetAttachedFileNameList(updatedFilesList);
    setRemovedFiles((prev) => [...prev, removedItem]);
  };

  const applyAlignmentPosition = (newStyle: any) => {
    const source = applyAlignment(newStyle, editorState);
    setEditorState(source);
  };
  const handleImageUrlModalOpen = () => {
    setImageLinkModalVisible(true)
  }
  const handleImageUrlClose = () => {
    setImageLinkModalVisible(false)
    setImageUrl("");
  }
  const handlePastedFiles = (files: Blob[]): DraftHandleValue => {
    const file = files[0];

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result as string;
      };
      reader.readAsDataURL(file);
      return "handled";
    }
    return "not-handled";
  };

  const handlePastedText = (
    text: string,
    html: string | undefined,
    editorState: EditorState
  ): DraftHandleValue => {
    if (text) {
      const contentState = editorState.getCurrentContent();
      const selection = editorState.getSelection();
      const newContentState = Modifier.replaceText(
        contentState,
        selection,
        text
      );

      const newEditorState = EditorState.push(editorState, newContentState, "insert-characters");
      setEditorState(newEditorState);
      return "handled";
    }
    return "not-handled";
  };
  return (
    <div>
      <div className='w-full h-auto border p-2'>
        {isComposeEmail &&
          <>
            <div className="flex items-center  border-b mt-6 justify-between">

              <div className='flex items-center w-[91%]'>
                <label className="block text-[#D34A7C] font-[400] text-[18px]  mb-1 font-rubik" htmlFor="to">
                  To:
                </label>
                <ReactMailChips emails={emails} setEmails={(newEmails) => handleEmailsChange('emails', newEmails)} className="font-rubik text-[16px]  w-full py-2 px-3 border-b-0 focus:outline-none focus:shadow-outline" />
              </div>

              {!openCc ? (
                <div className='flex  w-[9%]'>
                  <p className='px-2 text-[#707070] text-[17px] font-rubik cursor-pointer hover:underline' onClick={() => setOpenCc(true)}>Cc</p>
                  <p className='px-2 text-[#707070] text-[17px] font-rubik cursor-pointer hover:underline' onClick={() => setOpenBcc(true)}>Bcc</p>
                </div>
              ) : null}
            </div>

            {openCc && (
              <div className='border-b mt-2 flex items-center  justify-between'>
                <div className=' flex items-center w-[93%]'>
                  <label className="block text-[#D34A7C] font-[400] text-[18px]  mb-1 font-rubik" htmlFor="cc">
                    Cc:
                  </label>
                  <ReactMailChips emails={cc} setEmails={(newEmails) => handleEmailsChange('cc', newEmails)} className="font-rubik text-[16px]  w-full py-2 px-3 border-b-0 focus:outline-none focus:shadow-outline" />
                </div>
                {!openBcc ? (
                  <div className='flex  w-[7%]'>
                    <p className='px-2 text-[#707070] text-[17px] font-rubik cursor-pointer' onClick={() => setOpenBcc(true)}>Bcc</p>
                  </div>
                ) : null}
              </div>
            )}

            {openBcc && (
              <div className='border-b mt-2 flex items-center  justify-between'>
                <div className=' flex items-center w-[98%]'>
                  <label className="block text-[#D34A7C] font-[400] text-[18px]  mb-1 font-rubik" htmlFor="bcc">
                    Bcc:
                  </label>
                  <ReactMailChips emails={bcc} setEmails={(newEmails) => handleEmailsChange('bcc', newEmails)} className="font-rubik text-[16px]  w-full py-2 px-3 border-b-0 focus:outline-none focus:shadow-outline" />
                </div>
                <div>
                </div>
              </div>
            )}

            <div className="mb-2 flex items-center border-b py-4">
              <label className="block text-[#D34A7C] font-[400]  text-[18px] font-rubik" htmlFor="subject">
                Subject:
              </label>
              <input
                className="w-full  px-3 border-b-0 font-medium text-[17px] font-rubik focus:outline-none focus:shadow-outline"
                id="subject"
                type="text"
                placeholder=""
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
          </>}

        <div ref={containerRef} className={`body-content   ${iconModal ? "body-content-edit" : ""} `}>
          <input type="file" accept="image/*" id="announcementFileUpload" name="announcementFileUpload"
            onChange={handleImageUpload} style={{ display: 'none' }} multiple />
          <Editor
            onFocus={handleClickOutside}
            editorState={editorState}
            onChange={(newEditorState) => setEditorState(newEditorState)}
            handleKeyCommand={handleKeyCommand}
            plugins={plugins}
            blockStyleFn={blockStyleFn}
            handlePastedText={handlePastedText}
            handlePastedFiles={handlePastedFiles}
            placeholder={placeholder}
          />

          <FilesUploadedList
            attachedFile={attachedFile}
            getAttachedFileNameList={getAttachedFileNameList}
            iconModal={iconModal}
            handleRemoveAttachment={handleRemoveAttachment}
            removeAttachedFile={removeAttachedFile}
          />
        </div>
      </div>
      <div className={`flex button-footer ${textToolBar || mainToolbar ? "justify-between" : "justify-end"} border-t pt-[10px]`}>
        <Modal title="Insert Link" visible={linkModalVisible} onOk={addLink} onCancel={closeLinkModal}>
          <Input placeholder="Enter URL" value={linkURL} onChange={(e) => setLinkURL(e.target.value)} />
        </Modal>
        <Modal
          title="Insert Image URL"
          open={imageLinkModalVisible}
          onOk={handleInsertImageByURL}
          onCancel={handleImageUrlClose}
        >
          <Input
            placeholder="Enter Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
          />
        </Modal>
        {(iconModal || textToolBar) && (
          <EditorToolbar editorState={editorState} setEditorState={setEditorState} openCc={openCc}
            onUndo={onUndo} onRedo={onRedo} onBoldClick={onBoldClick} onItalicClick={onItalicClick}
            onUnderlineClick={onUnderlineClick} onBulletedListClick={onBulletedListClick}
            onNumberedListClick={onNumberedListClick} applyAlignmentPosition={applyAlignmentPosition}
            textToolBar={textToolBar}
          />
        )}

        {
          mainToolbar &&
          <>
            <ComposeMailToolbar
              handleIconModal={handleIconModal} handleFileSelect={handleFileSelect}
              formattedScheduleDate={formattedScheduleDate} iconModal={iconModal}
              formattedScheduleTime={formattedScheduleTime} openLinkModal={openLinkModal}
              handleImageUrlModalOpen={handleImageUrlModalOpen}
              draftButton={draftButton}
            />
            <div className='Dynamic-emoji'>
              <EmojiSuggestions />
              <EmojiSelect />
            </div>
          </>
        }

        <div className='space-x-2'>
          {draftButton && !(formattedScheduleDate && formattedScheduleTime) && (
            <Button
              loading={draftBtnLoading}
              className="cursor-pointer text-[#D34A7C] bg-[#FFF5F9] border border-[#D23B7D] w-[130px] h-[45px] rounded-3xl text-[18px] font-rubik hover:opacity-60 hover:transition-opacity save-btn"
              onClick={() => handleToSendMail('DRAFT')}
            >
              Draft
            </Button>
          )}

          {
            cancelButton &&
            <Button
              loading={draftBtnLoading}
              className="cursor-pointer text-[#D34A7C] bg-[#FFF5F9] border border-[#D23B7D] w-[130px] h-[45px] rounded-3xl text-[18px] font-rubik hover:opacity-60 hover:transition-opacity save-btn"
              onClick={() => handelCancel()}
            >
              Cancel
            </Button>
          }
          <Button loading={sendBtnLoading}
            className=' cursor-pointer text-white bg-[#D23B7D] w-[130px]  h-[45px] rounded-3xl text-[18px] font-rubik hover:opacity-70  hover:transition-opacity hover:text-white save-btn'
            onClick={() => handleToSendMail('SENT')}>
            {
              formattedScheduleDate && formattedScheduleTime ? 'Schedule' : submitButtonName
            }
          </Button>
        </div>
      </div>
      {popModal && <PopupModal message={modalErrMessage} open={popModal} handleModalClose={handleClosePopUpModal} response={modalResponse} modalButtonShow={modalButtonShow} />}

    </div >
  );
};
export default ComposeMail;